import React, { memo } from "react";
import { DeleteIcon, RepeatClockIcon } from "@chakra-ui/icons";
import { Box } from "@chakra-ui/layout";
import { HStack, IconButton, SkeletonText } from "@chakra-ui/react";
import { Table, Tbody, Td, Th, Tr, Thead } from "@chakra-ui/table";
import { Tag } from "@chakra-ui/tag";
import { CherryHubAdminApi } from "../../api/adminApiModels";
import { EditIcon } from "../../styles/icons";
import { CopyText } from "../Utilities/CopyText";
import DateUtil from "../../util/DateUtil";

interface VenuesTableProps {
  venues: CherryHubAdminApi.VenueSummaryItem[] | null;
  isLoading: boolean;
  debouncedQuery: string;
  disableActions?: boolean;
  onClickEditVenue: (venue: CherryHubAdminApi.VenueSummaryItem) => void;
  onClickDeleteVenue: (venue: CherryHubAdminApi.VenueSummaryItem) => void;
  onClickRestoreVenue: (venue: CherryHubAdminApi.VenueSummaryItem) => void;
}

export const VenuesTable = memo(
  ({
    venues,
    isLoading,
    disableActions,
    debouncedQuery,
    onClickEditVenue,
    onClickDeleteVenue,
    onClickRestoreVenue,
  }: VenuesTableProps) => {
    return (
      <Box w="100%" overflowX="auto">
        <Table size="sm">
          <Thead>
            <Tr>
              <Th width="10%" whiteSpace="nowrap">
                Venue Id
              </Th>
              <Th>Name</Th>
              <Th>Status</Th>
              <Th>Created</Th>
              <Th>Last Modified</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {!isLoading &&
              (venues ?? []).map((venue) => (
                <Tr key={venue.Meta!.Id}>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <CopyText
                      text={venue.Meta?.Id!}
                      copyOnIcon={true}
                      hover={true}
                    >
                      {venue.Meta?.Id}
                    </CopyText>
                  </Td>
                  <Td
                    title="Edit Venue."
                    _hover={
                      !venue.Meta?.IsDeleted
                        ? { textDecoration: "underline", cursor: "pointer" }
                        : {}
                    }
                    onClick={() =>
                      !venue.Meta?.IsDeleted && onClickEditVenue(venue)
                    }
                  >
                    {!!venue.Meta!.IsDeleted && (
                      <Tag size="sm" mr="2">
                        Deleted
                      </Tag>
                    )}
                    {venue.DisplayName}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <Tag
                      size="sm"
                      bgColor={venue.IsEnabled ? "" : "cherryButton.500"}
                      color={venue.IsEnabled ? "" : "white"}
                    >
                      {venue.IsEnabled ? "Enabled" : "Disabled"}
                    </Tag>
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      venue.Meta?.DateCreated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    {DateUtil.getLocalDateFormatFromString(
                      venue.Meta?.DateUpdated
                    )}
                  </Td>
                  <Td width="0.1%" whiteSpace="nowrap">
                    <HStack spacing={2} justifyContent="center">
                      {(venue.Meta!.IsDeleted && (
                        <IconButton
                          aria-label="Restore venue."
                          title="Restore venue."
                          icon={<RepeatClockIcon />}
                          size="xs"
                          isDisabled={disableActions}
                          onClick={() => onClickRestoreVenue(venue)}
                        />
                      )) || (
                        <IconButton
                          aria-label="Delete venue."
                          title="Delete venue."
                          icon={<DeleteIcon />}
                          size="xs"
                          isDisabled={disableActions}
                          onClick={() => onClickDeleteVenue(venue)}
                        />
                      )}
                    </HStack>
                  </Td>
                </Tr>
              ))}
            {venues?.length === 0 && !isLoading && (
              <Tr>
                <Td colSpan={5} textAlign="center">
                  No Venues found matching query '{debouncedQuery}'
                </Td>
              </Tr>
            )}
            {isLoading && (
              <Tr>
                <Td width="10%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="60%">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="5%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
                <Td width="10%" whiteSpace="nowrap">
                  <SkeletonText noOfLines={12} spacing="4" width="100%" />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
      </Box>
    );
  }
);
